// Si un nodo que casa el selector de enfocables puede recibir el foco de verdad.
//
// Las dos primeras comprobaciones cubren el caso real y son las únicas que entiende jsdom,
// donde corren los tests unitarios: un campo `type="hidden"` y cualquier cosa dentro de un
// subárbol apagado con el atributo `hidden`. `checkVisibility` añade lo que solo sabe un
// navegador —lo que esconde la hoja de estilos— y no existe en jsdom, así que ahí el `?.`
// la deja pasar. Esa parte la ejercita el feature spec `:js`, que tabula de verdad.
function esEnfocable (elemento) {
  if (elemento.disabled || elemento.type === 'hidden') return false
  if (elemento.closest('[hidden]')) return false

  return elemento.checkVisibility?.() !== false
}

class DialogModal extends HTMLElement {
  // Nombre del evento que emite al cerrarse por decisión del usuario.
  static EVENTO_CIERRE = 'dialog-modal:dismiss'

  constructor () {
    super()

    this.openTimeout = 300
    this.closeTimeout = 100
    this.contentLoading = false
    this.nextPage = 1
    this._onEscapePressed = this._onEscapePressed.bind(this)
    this._onTabPressed = this._onTabPressed.bind(this)
    this._onInfiniteScroll = this._onInfiniteScroll.bind(this)
    this._getSrcToFetch()
  }

  connectedCallback () {
    this._avoidDuplicating()
    // Por defecto, un dialog dentro de un form se queda donde está (su contenido
    // puede ser parte del form: campos, botones de submit…). El atributo opt-in
    // `move-to-body` fuerza el move-out incluso así, para diálogos cuyo contenido
    // NO depende del form (p. ej. texto legal de la política de privacidad).
    // Sin esto, un ancestor sticky/transform confina el `position: fixed` del
    // backlayer y elementos vecinos (iconos sociales, badges…) quedan por encima.
    if (this.hasAttribute('move-to-body') || !this.closest('form')) {
      this._moveToEndOfDocument()
    }
    this._setActions()
  }

  attributeChangedCallback (attribute) {
    var actionDo = (action) => {
      var actions = this.querySelector('dialog-actions')
      var calling = actions.querySelector(`[action="${action}"]`)

      if (this.hasAttribute(action)) {
        calling = calling || document.createElement('dialog-action')
        calling.setAttribute('action', action)
        calling.textContent = (this.getAttribute(action))
        calling.parentNode || actions.appendChild(calling)
      } else {
        calling && calling.parentNode.removeChild(calling)
      }
    }

    var properties = {
      dismiss: () => actionDo('dismiss'),
      confirm: () => actionDo('confirm'),
      open: () => {
        this.hasAttribute('open') ? this._open() : this.close()
      },
      closing: () => {
        if (this.hasAttribute('closing')) this.close()
      },
      die: () => {
        if (this.hasAttribute('die')) this.destroy()
      },
      closingx: () => {
        var calling, closingx = this.querySelector('dialog-close')

        if (this.hasAttribute('closingx')) {
          calling = closingx || document.createElement('dialog-close')
          calling.setAttribute('action', 'dismiss')
          calling.parentNode || this.querySelector('dialog-header')
            .insertBefore(calling, this.querySelector('dialog-title'))
        } else {
          calling = closingx
          calling && calling.parentNode.removeChild(calling)
        }
      }
    }

    return properties.hasOwnProperty(attribute) ? properties[attribute]() : undefined
  }

  static get observedAttributes() {
    return ['open', 'confirm', 'dismiss', 'closingx', 'closing', 'die']
  }

  get dialogBody () {
    return this.querySelector('dialog-body')
  }

  get admitInfiniteScroll () {
    return this.hasSrcToFetch && this.hasAttribute('infinite-scroll')
  }

  get hasSrcToFetch () {
    return this.fetchSrc
  }

  open () {
    this.setAttribute('open', '')
  }

  _open () {
    this.style.animation = `fade-zoom-in ${this.openTimeout}ms ease-in`

    this._addEventListeners()
    this._lockBody()
    this._anunciaQueEsModal()
    this._atrapaElFoco()

    if (this.hasSrcToFetch && !this.beingFetchedYet) {
      this._fetchContent()
    }
  }

  destroy () {
    this.close(true)
  }

  close (removeAfterClose = false) {
    this.style.animation = `fade-zoom-out ${this.closeTimeout}ms ease-out`

    // Se devuelve el foco YA, no dentro del setTimeout: si se va con el foco dentro y el
    // nodo sale del documento, el navegador lo manda al `body` y quien navega con teclado
    // vuelve al principio de la página.
    this._devuelveElFoco()

    setTimeout(() => {
      this.removeAttribute('open')
      this.removeAttribute('closing')
      this._removeEventListeners()
      this._unlockBody()

      // `parentNode` puede ser null si ya se quitó del DOM: dos cierres seguidos
      // (ESC dos veces, o ESC mientras se va cerrando por clic fuera) encolan dos
      // temporizadores y el segundo llega con el nodo ya retirado.
      if (removeAfterClose && this.parentNode) {
        this.parentNode.removeChild(this)
      }
    }, this.closeTimeout)
  }

  // `dialog-modal` no es el `<dialog>` nativo, así que no hereda nada de su accesibilidad:
  // ni se anuncia como ventana, ni atrapa el foco, ni lo devuelve (ENH-RNK-5363).
  //
  // Se anuncia al abrir y no en `connectedCallback` porque mientras está cerrada es
  // `display: none`: un `role="dialog"` permanente describiría como ventana algo que no
  // está en pantalla.
  _anunciaQueEsModal () {
    this.setAttribute('role', 'dialog')
    this.setAttribute('aria-modal', 'true')

    const titulo = this.querySelector('dialog-title')
    if (!titulo) return

    titulo.id = titulo.id || `${this.id || 'dialog-modal'}-titulo`
    this.setAttribute('aria-labelledby', titulo.id)
  }

  // Por defecto el foco va al contenedor, NO al primer campo: enfocar un input abriría el
  // teclado en móvil y desplazaría el cuerpo de la ventana, que es un cambio de
  // comportamiento visible. Al contenedor, quien usa lector de pantalla entra igual y quien
  // no, no nota nada. `preventScroll` evita el salto de scroll al enfocar.
  //
  // Con el atributo opt-in `focus-trap` el foco entra en un control: el marcado con
  // `data-autofocus` (en el modal de invitación, el CTA de crear cuenta: un Enter y estás
  // dentro, TC-134-1.1.10.1) o, si no hay ninguno, el primer enfocable.
  _atrapaElFoco () {
    this._focoPrevio = document.activeElement
    this.tabIndex = -1

    const control = this.hasAttribute('focus-trap') &&
      (this.querySelector('[data-autofocus]') || this._elementosEnfocables[0])

    if (control) {
      control.focus()
    } else {
      this.focus({ preventScroll: true })
    }
  }

  _devuelveElFoco () {
    this.removeAttribute('role')
    this.removeAttribute('aria-modal')

    // Puede haber desaparecido del documento mientras la ventana estaba abierta.
    if (this._focoPrevio && this._focoPrevio.isConnected) {
      this._focoPrevio.focus({ preventScroll: true })
    }

    this._focoPrevio = null
  }

  // Se recalculan en cada tabulación y no al abrir: el contenido puede llegar por `fetch`
  // después, y una lista cacheada dejaría fuera lo que se cargue luego.
  //
  // Descartar lo que NO puede recibir el foco es lo que hace que el atrapado funcione: el
  // selector de CSS casa también campos ocultos y paneles apagados con `hidden`, y si el
  // primero o el último de la lista es uno de ellos, `document.activeElement` no coincide
  // nunca con ellos y los dos guards de `_onTabPressed` quedan muertos. Es exactamente lo
  // que pasaba en la ventana de contacto de la ficha de escuela, que mantiene el formulario
  // y la confirmación en el DOM a la vez (ENH-RNK-5363): el primero era el campo oculto del
  // `authenticity_token` y el último el botón «Volver a la ficha» del panel apagado, así que
  // el foco se escapaba de la ventana en la primera tabulación.
  //
  // `escuelas_buscador_controller.js` resuelve lo mismo en su hoja de filtros de móvil.
  get _elementosEnfocables () {
    const enfocables = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'

    return Array.from(this.querySelectorAll(enfocables)).filter((elemento) => esEnfocable(elemento))
  }

  _lockBody () {
    document.body.style.overflow = 'hidden'
  }

  _unlockBody () {
    document.body.style.overflow = ''
  }

  _avoidDuplicating () {
    if (!this.id) return

    if (document.querySelectorAll('dialog-modal#' + this.id).length > 1) {
      this.parentNode.removeChild(this)
    }
  }

  _moveToEndOfDocument () {
    const estoyEnElDOM = this.isConnected
    const noEstoyAlFinalDelDocumento = this.parentNode !== document.body

    if (estoyEnElDOM && noEstoyAlFinalDelDocumento) {
      document.body.appendChild(this)
    }
  }

  _setActions (code) {
    if (this.hasAttribute('onconfirm')) {
      this._onConfirmCustomCode = this.getAttribute('onconfirm')
      this.removeAttribute('onconfirm')
    }

    if (this.hasAttribute('ondismiss')) {
      this._onDismissCustomCode = this.getAttribute('ondismiss')
      this.removeAttribute('ondismiss')
    }

    this.setAttribute('action', 'dismiss')
  }

  _addEventListeners () {
    this.addEventListener('click', this._onClick)
    document.addEventListener('keydown', this._onEscapePressed)
    document.addEventListener('keydown', this._onTabPressed)

    if (this.admitInfiniteScroll) {
      this.dialogBody.addEventListener('scroll', this._onInfiniteScroll)
    }
  }

  _removeEventListeners () {
    this.removeEventListener('click', this._onClick)
    document.removeEventListener('keydown', this._onEscapePressed)
    document.removeEventListener('keydown', this._onTabPressed)

    if (this.admitInfiniteScroll) {
      this.dialogBody.removeEventListener('scroll', this._onInfiniteScroll)
    }
  }

  // Único punto por el que pasan los TRES cierres sin decidir del diálogo
  // (aspa, clic en el overlay y ESC). Antes de cerrar avisa con un evento que
  // burbujea, para que quien necesite saber que se cerró no tenga que
  // reimplementar la detección de esos tres caminos por su cuenta —y no pueda
  // desviarse de lo que el diálogo hace de verdad—. Lo estrena la analítica del
  // modal de invitación (TC-134-1.1.10.3), que cuenta el cierre como un
  // desenlace más. El confirmar NO pasa por aquí (ver `_onConfirm`): confirmar
  // no es descartar, y este evento significa exactamente "cerró sin decidir".
  _onDismiss () {
    this.dispatchEvent(new CustomEvent(DialogModal.EVENTO_CIERRE, { bubbles: true }))

    this._onDismissCustomCode ? eval(this._onDismissCustomCode) : this.close(!this.hasAttribute('persist'))
  }

  _onConfirm () {
    // El fallback cierra directamente en vez de delegar en `_onDismiss` para no
    // emitir `dialog-modal:dismiss` en un confirm: si un diálogo con botón
    // `confirm` sin `onconfirm` custom lo emitiera, quien escuche el evento
    // contaría un "confirmar" como "cerrar sin decidir".
    this._onConfirmCustomCode ? eval(this._onConfirmCustomCode) : this.close(!this.hasAttribute('persist'))
  }

  _onEscapePressed (event) {
    if (event.key === 'Escape') {
      this._onDismiss()
    }
  }

  // Sin esto el tabulador se sale de la ventana y sigue recorriendo la página de detrás,
  // que está tapada por el backlayer: el foco desaparece de la vista.
  _onTabPressed (event) {
    if (event.key !== 'Tab') return

    const enfocables = this._elementosEnfocables
    if (enfocables.length === 0) return

    const primero = enfocables[0]
    const ultimo = enfocables[enfocables.length - 1]
    const salePorDetras = event.shiftKey && document.activeElement === primero
    const salePorDelante = !event.shiftKey && document.activeElement === ultimo

    // Estando el foco en el contenedor (recién abierta), la primera tabulación entra en el
    // contenido por su cuenta: el navegador sigue el orden del documento.
    if (!salePorDetras && !salePorDelante) return

    event.preventDefault()
    ;(salePorDetras ? ultimo : primero).focus()
  }

  _onInfiniteScroll (event) {
    if (event.target.scrollTop + event.target.clientHeight >= event.target.scrollHeight) {
      this._fetchContent()
    }
  }

  _onClick (event) {
    event.stopPropagation()

    const targetIsDialogActionButton = event.target.tagName === 'dialog-action' || event.target.closest('dialog-action')
    const action = event.target.getAttribute('action')

    if (targetIsDialogActionButton && action === 'confirm') {
      this._onConfirm()
    } else if (action === 'dismiss') {
      this._onDismiss()
    }
  }

  _fetchContent () {
    if (this.contentLoading) return

    this.dialogBody.innerHTML += this._spinnerHTML()
    this.contentLoading = true
    this.beingFetchedYet = true

    fetch(this.admitInfiniteScroll ? this._buildPaginatedURL() : this.fetchSrc)
      .then(response => response.text())
      .then((html) => {
        this.dialogBody.removeChild(this.dialogBody.lastChild)
        this.dialogBody.innerHTML += html
        this.nextPage += 1
        this.contentLoading = false
      })
  }

  _getSrcToFetch () {
    if (this.hasAttribute('src')) {
      this.fetchSrc = this.getAttribute('src')
      this.removeAttribute('src')
    } else if (this.hasAttribute('encodedsrc')) {
      this.fetchSrc = atob(this.getAttribute('encodedsrc'))
      this.removeAttribute('encodedsrc')
    }
  }

  _buildPaginatedURL () {
    const url = new URL(this.fetchSrc)
    const conector = url.search ? '&' : '?'

    url.search += conector + `page=${this.nextPage}`

    return url
  }

  _spinnerHTML () {
    return '<div class="rnk-align-center rnk-margin-bottom-large"><i class="rnk-Icon-spinner-inline"></div>'
  }
}

export default DialogModal
